/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useCallback, useEffect } from 'react';
import { useLogger } from '@airasia-rn/core-logger';
import {
  buildErrorEventParams,
  ERROR_EVENT,
  ERROR_TYPE,
} from '../config/analytics';
import {
  clearSchemaReporter,
  setSchemaReporter,
  type SchemaMismatch,
  type SchemaMismatchReporter,
} from '../schema/schemaReporter';

/** `<path>: expected X, got Y` — the path alone is the report's middle segment. */
const pathOf = (issue: string): string => issue.split(':')[0];

class SchemaMismatchError extends Error {
  constructor(mismatch: SchemaMismatch) {
    super(
      `${mismatch.endpoint} → ${mismatch.key}: ${mismatch.issues.join('; ')}`,
    );
    this.name = 'SchemaMismatchError';
  }
}

/**
 * Hands the app's logger to the API services, which validate their responses
 * but cannot reach a React context to report what they find.
 *
 * `logEvent`/`logError` rather than `logWarn`: those are the two members
 * `createWebLogger` implements, so they are what reaches GTM from the logger
 * account-web mounts. Called from `useApiQuery`/`useApiMutation` so every
 * account fetch installs it and no host has to mount a provider.
 */
export const useSchemaReporter = (): void => {
  const { logEvent, logError } = useLogger();

  const report = useCallback<SchemaMismatchReporter>(
    (mismatch) => {
      const [first] = mismatch.issues;
      const extra = mismatch.issueCount - 1;

      logEvent(
        ERROR_EVENT,
        buildErrorEventParams({
          type: ERROR_TYPE.validation,
          code: mismatch.key,
          description: mismatch.endpoint,
          message: `${first ?? 'invalid payload'}${extra > 0 ? ` (+${extra} more)` : ''}`,
        }),
      );

      logError(new SchemaMismatchError(mismatch), {
        context: 'Response schema mismatch',
        schemaKey: mismatch.key,
        endpoint: mismatch.endpoint,
        severity: mismatch.severity,
        field: first ? pathOf(first) : '',
        issueCount: mismatch.issueCount,
        issues: mismatch.issues.join('; '),
        occurrences: mismatch.occurrences,
      });
    },
    [logEvent, logError],
  );

  useEffect(() => {
    setSchemaReporter(report);
    return () => clearSchemaReporter(report);
  }, [report]);
};
